import React from "react";
import SVG from "react-inlinesvg";
import ReactModal from "react-modal";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconXClose from "../../../img/icons/x-close.svg";
import { focusElement, trapFocus } from "../../utils/keyboardFocus";
import { usePageSetting } from "../../utils/settings";
import { ChildrenResizeSensor } from "../ChildrenResizeSensor";

import styles from "./index.module.scss";

export interface IProps extends ReactModal.Props {
    closeBtnProps?: React.DetailedHTMLProps<
        React.ButtonHTMLAttributes<HTMLButtonElement>,
        HTMLButtonElement
    >;
    closeBtnContent?: React.ReactNode;
    closeBtnIcon?: string;
    preCloseBtnContent?: React.ReactNode;
    disableHeightExpansion?: boolean;
    ref?: React.Ref<ModalRef>;
}

// Forward some types from react-modal
export type ModalStyles = ReactModal.Styles;

// Reset defaults
ReactModal.defaultStyles = {
    overlay: {},
    content: {},
};

// Helper function to expand portal height to meet or exceed the bottom of the modal content
const expandPortalHeight = (
    portalElem: HTMLElement,
    contentElem: HTMLDivElement,
): void => {
    const window = getWindow();
    const portalRect = portalElem.getBoundingClientRect();
    const modalContentRect = contentElem.getBoundingClientRect();
    const portalBottom = (window?.scrollX ?? 0) + portalRect.bottom;
    const modalContentBottom = (window?.scrollX ?? 0) + modalContentRect.bottom;
    // Adjust the height of the portal so that its at or below the height of the modal content. This ensures
    // that the modal content is never taller than the page itself (even though the content is absolutely
    // positioned, and therefore doesn't otherwise affect the page layout at all).
    const delta = portalRect.height + (modalContentBottom - portalBottom);
    portalElem.style.height = `${Math.max(0, delta)}px`;
};

// Helper function to collapse portal height back to default
const collapsePortalHeight = (portalElem: HTMLElement | null): void => {
    if (portalElem) {
        portalElem.style.height = "";
    }
};

// Imperative handle interface for exposing refs to parent components
export interface ModalRef {
    closeBtn: React.RefObject<HTMLButtonElement | null>;
    contentElem: React.RefObject<HTMLDivElement | null>;
    portalElem: React.RefObject<HTMLElement | null>;
}

export const Modal = (props: IProps): React.ReactNode => {
    const { ref, ...otherProps } = props;
    const closeBtnRef = React.useRef<HTMLButtonElement>(null);
    const contentElemRef = React.useRef<HTMLDivElement | null>(null);
    const portalElemRef = React.useRef<HTMLElement | null>(null);
    const isOpenRef = React.useRef<boolean>(false);

    const appSlug = usePageSetting("app_slug");

    // Expose refs to parent components through ref forwarding
    React.useImperativeHandle(ref, () => {
        return {
            closeBtn: closeBtnRef,
            contentElem: contentElemRef,
            portalElem: portalElemRef,
        };
    });

    const handleAfterOpen: ReactModal.OnAfterOpenCallback = (opts): void => {
        isOpenRef.current = true;
        // Expand the body height to meet or exceed the bottom of the modal content
        if (
            !props.disableHeightExpansion &&
            portalElemRef.current &&
            contentElemRef.current
        ) {
            expandPortalHeight(portalElemRef.current, contentElemRef.current);
        }
        // Prevent scroll on focus in Sealy
        const options = {
            preventScroll: appSlug === "sealy",
        };
        // Set keyboard focus to the close button
        if (closeBtnRef.current) {
            focusElement(closeBtnRef.current, options);
        }
        // Trap focus inside the modal
        if (contentElemRef.current) {
            trapFocus(contentElemRef.current);
        }
        // Bubble up event to parent component
        if (props.onAfterOpen) {
            props.onAfterOpen(opts);
        }
    };

    const handleAfterClose = (): void => {
        isOpenRef.current = false;
        // Collapse the portal back to its default, zero-height state.
        collapsePortalHeight(portalElemRef.current);
        // Bubble up event to parent component
        if (props.onAfterClose) {
            props.onAfterClose();
        }
    };

    const handleResize = (): void => {
        if (
            isOpenRef.current &&
            !props.disableHeightExpansion &&
            portalElemRef.current &&
            contentElemRef.current
        ) {
            expandPortalHeight(portalElemRef.current, contentElemRef.current);
        }
    };

    const setContentRef = (elem: HTMLDivElement | null): void => {
        contentElemRef.current = elem;
        // Bubble up event to parent component
        if (otherProps.contentRef && elem) {
            otherProps.contentRef(elem);
        }
    };

    const setOverlayRef = (elem: HTMLDivElement | null): void => {
        // Cache the portal elem too (but purposefully don't overwrite it if overlay is null).
        // This allows us to zero out the portal height after the overlay elem is removed.
        if (!elem) {
            return;
        }
        portalElemRef.current = elem.parentElement;
        // Bubble up event to parent component
        if (otherProps.overlayRef) {
            otherProps.overlayRef(elem);
        }
    };

    return (
        <ReactModal
            aria={{
                modal: true,
            }}
            role="dialog"
            {...otherProps}
            contentRef={setContentRef}
            overlayRef={setOverlayRef}
            onAfterOpen={handleAfterOpen}
            onAfterClose={handleAfterClose}
        >
            {props.preCloseBtnContent}
            <button
                type="button"
                aria-label={t`Close`}
                className={`${styles.modalCloseBtn} ${
                    props.closeBtnProps?.className || ""
                }`}
                {...props.closeBtnProps}
                ref={closeBtnRef}
                onClick={props.onRequestClose}
            >
                {props.closeBtnContent || (
                    <SVG
                        className={styles.modalCloseIcon}
                        src={props.closeBtnIcon || iconXClose}
                        aria-hidden="true"
                    />
                )}
            </button>
            <ChildrenResizeSensor onResize={handleResize}>
                {props.children}
            </ChildrenResizeSensor>
        </ReactModal>
    );
};

Modal.displayName = "Modal";
